import { createFileRoute, Link } from "@tanstack/react-router";
import { Flag, ShieldCheck, FileText, Mail } from "lucide-react";
import { CompanyInfo } from "@/components/CompanyInfo";

export const Route = createFileRoute("/us-privacy-rights")({
  head: () => ({
    meta: [
      { title: "US Privacy Rights Notice — Recovery Trauma™" },
      {
        name: "description",
        content:
          "California (CCPA/CPRA) and US state privacy rights, HIPAA positioning, and how to exercise your privacy choices on Recovery Trauma™.",
      },
      { property: "og:title", content: "US Privacy Rights Notice — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "How Recovery Trauma™ handles US state privacy rights, HIPAA, COPPA, and consumer protection requirements.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/us-privacy-rights" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/us-privacy-rights" },
    ],
  }),
  component: UsPrivacyRightsPage,
});

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/15 text-gold">
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <h2 className="font-serif text-xl text-foreground">{title}</h2>
      </div>
      <div className="space-y-2 text-sm leading-relaxed text-foreground/85">
        {children}
      </div>
    </section>
  );
}

function UsPrivacyRightsPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8 mt-4 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <Flag className="h-3.5 w-3.5" /> United States
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          US Privacy Rights Notice
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
          How Recovery Trauma™ handles your privacy under US federal and state
          consumer protection laws.
        </p>
      </header>

      <div className="space-y-5">
        <Section icon={ShieldCheck} title="Positioning under US law">
          <p>
            Recovery Trauma™ is a trauma-informed wellbeing, education,
            self-help, therapist discovery, and peer-community platform. It is{" "}
            <strong>not</strong> a healthcare provider, medical platform,
            clinical treatment service, or diagnostic service, and it does not
            provide therapy.
          </p>
          <p>
            Recovery Trauma™ operates as a wellbeing, education, self-help,
            community and therapist-discovery platform and does not represent
            itself as a HIPAA-covered healthcare provider or business associate.
            The applicability of HIPAA and other US privacy laws can depend on
            the nature of the entity, the information and the processing
            involved. Where you choose to share information directly with a
            therapist listed in our directory, that therapist is independently
            responsible for their own HIPAA, state and professional
            obligations.
          </p>
        </Section>

        <Section icon={FileText} title="California rights (CCPA / CPRA)">
          <p>
            If you are a California resident, you have the right to:
          </p>
          <ul className="list-inside list-disc space-y-1">
            <li>Know what personal information we collect and how it is used</li>
            <li>Access a copy of the personal information we hold about you</li>
            <li>Correct inaccurate personal information</li>
            <li>Delete personal information we hold about you</li>
            <li>Limit the use of sensitive personal information</li>
            <li>
              Opt out of any "sale" or "sharing" of personal information —
              Recovery Trauma™ does not sell or share your personal information
              for cross-context behavioural advertising
            </li>
            <li>Non-discrimination for exercising any of these rights</li>
          </ul>
          <p>
            You can exercise these rights from{" "}
            <Link to="/account/privacy" className="text-gold underline">
              Account → Privacy
            </Link>{" "}
            (data export and account deletion) or by emailing us at the address
            below. We will respond within the timeframes required by law.
          </p>
        </Section>

        <Section icon={FileText} title="Other US state privacy laws">
          <p>
            Residents of states with comparable privacy laws — including
            Colorado (CPA), Connecticut (CTDPA), Virginia (VCDPA), Utah (UCPA),
            Texas (TDPSA), Oregon (OCPA), and others — have substantially
            similar rights to access, correct, delete, and port personal
            information, and to opt out of targeted advertising or profiling
            with legal effect. Recovery Trauma™ honours these rights in the
            same way as California rights described above.
          </p>
        </Section>

        <Section icon={ShieldCheck} title="Children's privacy (COPPA)">
          <p>
            Recovery Trauma™ is intended for users aged 18 years or older. We
            do not knowingly collect personal information from children under
            13. If you believe a child has provided us with personal
            information, please contact us and we will delete it.
          </p>
        </Section>

        <Section icon={ShieldCheck} title="Therapist directory & state licensing">
          <p>
            Recovery Trauma™ provides a directory of independent professionals.
            Any therapy, coaching, counselling, or professional services are
            provided solely by the individual practitioner and not by Recovery
            Trauma™. US therapists may only practise in states where they are
            licensed unless specific legal exceptions apply — please confirm
            licensure and jurisdiction directly with the practitioner before
            engaging their services.
          </p>
        </Section>

        <Section icon={Mail} title="How to contact us">
          <p>
            To exercise any US privacy right, or to ask a question about this
            notice, please contact:
          </p>
          <p>
            <a
              href="mailto:info@recoverytrauma.com"
              className="font-semibold text-gold underline-offset-2 hover:underline"
            >
              info@recoverytrauma.com
            </a>
          </p>
          <div className="mt-3">
            <CompanyInfo />
          </div>
        </Section>
      </div>

      <p className="mt-8 text-center text-xs text-muted-foreground">
        This notice is provided as a template and should be reviewed by your
        legal counsel before launch. Last updated: June 2026.
      </p>
    </div>
  );
}
